/**
 * @file useComicTip.js
 * @description 漫画打赏弹窗：金额档位、预下单、PC 二维码 / 移动端复制链接 / JSAPI
 * @module features/comic/composables/useComicTip
 */

import { computed, ref } from 'vue'
import QRCode from 'qrcode'
import {
  PAY_ORDER_TYPE_TIP,
  fetchTipAmountListApi,
  prepareTipWechatApi,
  queryTipOrderStatusByIdApi,
  queryTipOrderStatusByOutTradeNoApi,
  TipWxMpOAuthRequiredError,
} from '@/features/comic/api/tipApi'
import { getPayScene, isMobileExternalBrowser, toWechatPayScene } from '@/shared/utils/platform'
import { copyTextToClipboard } from '@/shared/utils/clipboard'
import { buildTipShareUrl } from '@/features/comic/utils/tipShareUrl'
import { getAuthToken } from '@/shared/utils/auth'
import { ERR_WX_MP_OAUTH_REQUIRED, getApiErrorMessageByCode } from '@/shared/utils/apiErrorCodes'
import { handleWxMpOAuthRequiredResponse, getStoredUserId, getStoredUserPhone, hasPendingTipOAuthAutoRetried, markPendingTipOAuthAutoRetried, clearPendingTipOAuthAutoRetried } from '@/shared/utils/wxMpOAuth'
import { getStoredUser, setStoredUser } from '@/shared/utils/storage'
import { invokeWeChatPay } from '@/shared/utils/wechatPay'
import { createTipIdempotencyKey } from '@/features/comic/utils/tipIdempotency'
import {
  clearPendingTipResume,
  consumePendingTipResume,
  isTipResumeSessionDone,
  markTipResumeSessionDone,
  peekPendingTipResume,
  savePendingTipResume,
} from '@/features/comic/utils/tipPendingOAuth'
import { isTipOrderClosed, isTipOrderPaid } from '@/features/comic/utils/tipOrderStatus'
import { formatTipAmountLabel } from '@/features/comic/utils/tipAmountFormat'

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 5 * 60 * 1000

/** @typedef {'select' | 'qrcode' | 'success'} TipDialogStep */

/** @typedef {import('@/features/comic/api/tipTypes').TipAmountOptionVo} TipAmountOption */

/** @typedef {Object} TipOpenContext
 * @property {string} comicId
 * @property {string} [comicTitle]
 * @property {'detail' | 'reader'} source
 * @property {string} [volId]
 * @property {number} [volNum]
 */

/** @typedef {Object} TipOpenOptions
 * @property {string} [tipCode] 预选打赏档位
 */

const visible = ref(false)
const step = ref(/** @type {TipDialogStep} */ ('select'))
const tipAmountOptions = ref(/** @type {TipAmountOption[]} */ ([]))
const tipAmountLoading = ref(false)
const selectedTipCode = ref('')
const loading = ref(false)
const errorMessage = ref('')
const guideMessage = ref('')
const guideShareUrl = ref('')
const qrDataUrl = ref('')
const currentOrderId = ref('')
const currentOutTradeNo = ref('')
const isConfirmingPayment = ref(false)
const payScene = ref(/** @type {import('@/shared/utils/platform').PayScene} */ ('PC_NATIVE'))

/** @type {TipOpenContext | null} */
let openContext = null
let pollTimer = null
let pollStartedAt = 0
let onVisibilityRefresh = null
let pollConsecutiveFailures = 0
/** @type {string} */
let idempotencyKey = ''
/** 打赏预下单进行中（同步互斥，防双次调起支付） */
let tipSubmitInFlight = false
/** OAuth 回站续打赏进行中 */
let tipResumeInFlight = false

/**
 * @param {unknown} err
 * @returns {string}
 */
function resolveTipError(err) {
  const code = err && typeof err === 'object' && 'code' in err ? err.code : null
  const byCode = getApiErrorMessageByCode(code)
  if (byCode) return byCode
  if (err instanceof Error && err.message) return err.message
  return '操作失败，请稍后再试'
}

/**
 * 默认选中档位：有多档时选第二档，否则选第一档
 * @param {TipAmountOption[]} options
 * @returns {string}
 */
function pickDefaultTipCode(options) {
  if (!options.length) return ''
  const index = options.length >= 2 ? 1 : 0
  return options[index].code
}

/**
 * @param {TipAmountOption[]} options
 * @returns {TipAmountOption | null}
 */
function findSelectedTipOption(options) {
  if (!selectedTipCode.value) return null
  return options.find((opt) => opt.code === selectedTipCode.value) ?? null
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
  if (onVisibilityRefresh && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityRefresh)
    onVisibilityRefresh = null
  }
  isConfirmingPayment.value = false
}

function resetIdempotencyKey() {
  idempotencyKey = ''
}

function ensureIdempotencyKey() {
  if (!idempotencyKey) {
    idempotencyKey = createTipIdempotencyKey()
  }
  return idempotencyKey
}

function resetDialog() {
  stopPolling()
  step.value = 'select'
  tipAmountOptions.value = []
  tipAmountLoading.value = false
  selectedTipCode.value = ''
  loading.value = false
  errorMessage.value = ''
  guideMessage.value = ''
  guideShareUrl.value = ''
  qrDataUrl.value = ''
  currentOrderId.value = ''
  currentOutTradeNo.value = ''
  isConfirmingPayment.value = false
  openContext = null
  resetIdempotencyKey()
}

/**
 * 拉取打赏档位列表
 * @param {string} [presetTipCode]
 * @returns {Promise<void>}
 */
async function loadTipAmountOptions(presetTipCode = '') {
  tipAmountLoading.value = true
  errorMessage.value = ''
  try {
    const items = await fetchTipAmountListApi()
    const valid = items.filter((item) => item.code && item.amountCent > 0)
    tipAmountOptions.value = valid
    const preset = typeof presetTipCode === 'string' ? presetTipCode.trim() : ''
    if (preset && valid.some((item) => item.code === preset)) {
      selectedTipCode.value = preset
    } else {
      selectedTipCode.value = pickDefaultTipCode(valid)
    }
    if (!valid.length) {
      errorMessage.value = '暂无可选打赏金额，请稍后再试'
    }
  } catch (err) {
    tipAmountOptions.value = []
    selectedTipCode.value = ''
    errorMessage.value = resolveTipError(err)
  } finally {
    tipAmountLoading.value = false
  }
}

/**
 * 打开打赏弹窗（须已登录）
 * @param {TipOpenContext} context
 * @param {TipOpenOptions} [options]
 * @returns {boolean} 是否已打开弹窗
 */
export function openComicTip(context, options = {}) {
  if (!getAuthToken()) return false
  stopPolling()
  openContext = context
  step.value = 'select'
  errorMessage.value = ''
  guideMessage.value = ''
  guideShareUrl.value = ''
  qrDataUrl.value = ''
  currentOrderId.value = ''
  currentOutTradeNo.value = ''
  payScene.value = getPayScene()
  resetIdempotencyKey()
  visible.value = true
  void loadTipAmountOptions(options.tipCode)
  return true
}

export function closeComicTip() {
  visible.value = false
  resetDialog()
}

/**
 * @param {string} tipCode
 */
function selectTipCode(tipCode) {
  if (selectedTipCode.value !== tipCode) {
    selectedTipCode.value = tipCode
    resetIdempotencyKey()
  }
  errorMessage.value = ''
  guideMessage.value = ''
  guideShareUrl.value = ''
}

/**
 * @param {string} codeUrl
 */
async function renderQrCode(codeUrl) {
  qrDataUrl.value = await QRCode.toDataURL(codeUrl, {
    width: 200,
    margin: 2,
    errorCorrectionLevel: 'M',
  })
}

/**
 * 查询一次订单状态并处理 UI 切换
 * @returns {Promise<boolean>} 是否已支付成功
 */
async function pollOrderStatusOnce() {
  try {
    let statusVo
    if (currentOrderId.value) {
      statusVo = await queryTipOrderStatusByIdApi({ orderId: currentOrderId.value })
    } else if (currentOutTradeNo.value) {
      statusVo = await queryTipOrderStatusByOutTradeNoApi({
        outTradeNo: currentOutTradeNo.value,
      })
    } else {
      return false
    }
    pollConsecutiveFailures = 0

    if (statusVo?.orderId) {
      currentOrderId.value = String(statusVo.orderId)
    }
    if (statusVo?.outTradeNo) {
      currentOutTradeNo.value = String(statusVo.outTradeNo)
    }

    if (isTipOrderPaid(statusVo)) {
      stopPolling()
      loading.value = false
      step.value = 'success'
      resetIdempotencyKey()
      return true
    }
    if (isTipOrderClosed(statusVo)) {
      stopPolling()
      loading.value = false
      errorMessage.value = '支付失败或订单已关闭，请重试'
      step.value = 'select'
      resetIdempotencyKey()
      return false
    }
  } catch (err) {
    pollConsecutiveFailures += 1
    if (pollConsecutiveFailures >= 3 && loading.value) {
      console.warn('[tip] poll order status failed', err)
      errorMessage.value = resolveTipError(err) || '查询支付结果失败，请稍后再试'
    }
  }
  return false
}

/**
 * 支付完成后立即查单若干次（配合后端微信查单补偿）
 * @param {number} [times=2]
 */
async function pollOrderStatusBurst(times = 2) {
  for (let i = 0; i < times; i += 1) {
    const paid = await pollOrderStatusOnce()
    if (paid) return true
  }
  return false
}

/**
 * 开始轮询订单支付结果（立即查一次，再定时查）
 * @param {string} orderId
 * @param {string} [outTradeNo]
 */
function startOrderPolling(orderId, outTradeNo = '') {
  stopPolling()
  currentOrderId.value = orderId ? String(orderId) : ''
  currentOutTradeNo.value = outTradeNo ? String(outTradeNo) : ''
  pollStartedAt = Date.now()
  pollConsecutiveFailures = 0

  void pollOrderStatusOnce()

  pollTimer = setInterval(async () => {
    if (Date.now() - pollStartedAt > POLL_TIMEOUT_MS) {
      stopPolling()
      loading.value = false
      errorMessage.value = '支付确认超时，若已付款请稍后查看消费记录'
      return
    }
    await pollOrderStatusOnce()
  }, POLL_INTERVAL_MS)

  if (typeof document !== 'undefined') {
    onVisibilityRefresh = () => {
      if (document.visibilityState !== 'visible') return
      const shouldPoll =
        step.value === 'qrcode' || (step.value === 'select' && loading.value)
      if (shouldPoll) {
        void pollOrderStatusOnce()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityRefresh)
  }
}

/** 用户扫码后手动触发查单 */
async function confirmPaymentManually() {
  if (!currentOrderId.value && !currentOutTradeNo.value) return
  if (isConfirmingPayment.value) return
  isConfirmingPayment.value = true
  errorMessage.value = ''
  try {
    const paid = await pollOrderStatusOnce()
    if (!paid && step.value === 'qrcode') {
      errorMessage.value = '暂未查询到支付结果，请稍后再试'
    }
  } finally {
    if (step.value === 'qrcode') {
      isConfirmingPayment.value = false
    }
  }
}

async function submitTip() {
  if (tipSubmitInFlight) {
    console.info('[tip-pay] submitTip skip: inFlight')
    return
  }
  if (!openContext || loading.value || tipAmountLoading.value) {
    console.info('[tip-pay] submitTip skip: notReady')
    return
  }
  if (!selectedTipCode.value) {
    errorMessage.value = '请选择打赏金额'
    return
  }

  if (isMobileExternalBrowser()) {
    tipSubmitInFlight = true
    errorMessage.value = ''
    guideMessage.value = ''
    guideShareUrl.value = ''
    const shareUrl = buildTipShareUrl({
      comicId: openContext.comicId,
      source: openContext.source,
      volNum: openContext.volNum,
      tipCode: selectedTipCode.value,
    })
    if (!shareUrl) {
      errorMessage.value = '无法生成分享链接，请稍后再试'
      tipSubmitInFlight = false
      return
    }
    const copied = await copyTextToClipboard(shareUrl)
    if (copied) {
      guideMessage.value = '链接已复制，请在微信内打开完成打赏'
    } else {
      guideMessage.value = '请复制以下链接，在微信内打开完成打赏'
      guideShareUrl.value = shareUrl
    }
    tipSubmitInFlight = false
    return
  }

  tipSubmitInFlight = true
  errorMessage.value = ''
  guideMessage.value = ''
  guideShareUrl.value = ''
  loading.value = true
  console.info('[tip-pay] submitTip start')
  try {
    const clientScene = getPayScene()
    payScene.value = clientScene
    const apiScene = toWechatPayScene(clientScene)

    /** @type {import('@/features/comic/api/tipTypes').PrepareTipWechatPayload} */
    const payload = {
      orderType: PAY_ORDER_TYPE_TIP,
      scene: apiScene,
      comicId: String(openContext.comicId),
      tipCode: selectedTipCode.value,
      idempotencyKey: ensureIdempotencyKey(),
    }
    if (openContext.volId) {
      payload.volId = String(openContext.volId)
    }

    if (apiScene === 'JSAPI') {
      savePendingTipResume({
        comicId: String(openContext.comicId),
        comicTitle: openContext.comicTitle || '',
        source: openContext.source,
        volId: openContext.volId,
        tipCode: selectedTipCode.value,
        idempotencyKey: ensureIdempotencyKey(),
        expectedUserId: getStoredUserId(),
        expectedUserPhone: getStoredUserPhone(),
        preOAuthSession: getStoredUser(),
      })
    }

    const order = await prepareTipWechatApi(payload)
    clearPendingTipResume()
    clearPendingTipOAuthAutoRetried()
    currentOrderId.value = order.orderId ? String(order.orderId) : ''
    currentOutTradeNo.value = order.outTradeNo ? String(order.outTradeNo) : ''
    let keepLoadingForPoll = false

    if (apiScene === 'NATIVE') {
      if (!order.codeUrl) {
        throw new Error('未获取到支付二维码，请稍后再试')
      }
      await renderQrCode(order.codeUrl)
      step.value = 'qrcode'
      if (order.orderId || order.outTradeNo) {
        startOrderPolling(
          order.orderId ? String(order.orderId) : '',
          order.outTradeNo ? String(order.outTradeNo) : ''
        )
      }
    } else if (apiScene === 'JSAPI') {
      if (!order.jsapi) {
        throw new Error('未获取到微信支付参数')
      }
      await invokeWeChatPay({
        appId: order.jsapi.appId,
        timeStamp: order.jsapi.timeStamp,
        nonceStr: order.jsapi.nonceStr,
        package: order.jsapi.package,
        signType: order.jsapi.signType,
        paySign: order.jsapi.paySign,
      })
      if (order.orderId || order.outTradeNo) {
        keepLoadingForPoll = true
        startOrderPolling(
          order.orderId ? String(order.orderId) : '',
          order.outTradeNo ? String(order.outTradeNo) : ''
        )
        await pollOrderStatusBurst(2)
      }
    }

    if (!keepLoadingForPoll) {
      loading.value = false
    }
  } catch (err) {
    loading.value = false
    if (err instanceof Error && err.message === '用户取消支付') {
      errorMessage.value = '已取消支付'
      clearPendingTipResume()
    } else if (err instanceof TipWxMpOAuthRequiredError) {
      const redirected = handleWxMpOAuthRequiredResponse({
        code: ERR_WX_MP_OAUTH_REQUIRED,
        data: { oauthAuthorizeUrl: err.oauthAuthorizeUrl },
      })
      if (!redirected) {
        errorMessage.value =
          err.oauthAuthorizeUrl?.trim() && !err.oauthAuthorizeUrl.includes('bindTicket=')
            ? '微信授权链接无效，请重新登录后再试'
            : '需要微信授权，请重试'
        clearPendingTipResume()
        resetIdempotencyKey()
      }
      return
    } else {
      clearPendingTipResume()
      errorMessage.value = resolveTipError(err)
    }
    resetIdempotencyKey()
  } finally {
    tipSubmitInFlight = false
    /* JSAPI 轮询时 loading 由轮询回调关闭 */
  }
}

function backToSelect() {
  stopPolling()
  step.value = 'select'
  qrDataUrl.value = ''
  errorMessage.value = ''
  guideMessage.value = ''
  guideShareUrl.value = ''
  resetIdempotencyKey()
}

/**
 * OAuth 回站后恢复打赏弹窗并自动续预下单（若有 pendingTip）
 * @param {{ fromOAuthError?: string | null, keepPending?: boolean }} [options]
 * @returns {Promise<boolean>}
 */
export async function resumePendingTipAfterOAuth(options = {}) {
  const { fromOAuthError = null, keepPending = false } = options
  const resumeSource = fromOAuthError ? 'oauthError' : 'oauthSuccess'

  if (tipResumeInFlight) {
    console.info('[tip-pay] resume skip: inFlight source=%s', resumeSource)
    return false
  }

  const pending = keepPending ? peekPendingTipResume() : consumePendingTipResume()
  if (!pending || !getAuthToken()) {
    console.info('[tip-pay] resume skip: noPendingOrToken source=%s', resumeSource)
    return false
  }

  const resumeSessionId =
    typeof pending.resumeSessionId === 'string' ? pending.resumeSessionId.trim() : ''
  if (resumeSessionId && isTipResumeSessionDone(resumeSessionId)) {
    console.info('[tip-pay] resume skip: sessionDone source=%s sessionId=%s', resumeSource, resumeSessionId)
    return false
  }

  tipResumeInFlight = true
  console.info('[tip-pay] resume start source=%s sessionId=%s', resumeSource, resumeSessionId || 'none')

  const currentPhone = getStoredUserPhone()
  const currentUserId = getStoredUserId()
  const phoneMismatch =
    Boolean(pending.expectedUserPhone) && pending.expectedUserPhone !== currentPhone
  const userIdMismatch =
    Boolean(pending.expectedUserId) && currentUserId && pending.expectedUserId !== currentUserId
  if (phoneMismatch || userIdMismatch) {
    if (pending.preOAuthSession && typeof pending.preOAuthSession === 'object') {
      setStoredUser(pending.preOAuthSession)
    }
    openContext = {
      comicId: String(pending.comicId),
      comicTitle: pending.comicTitle || '',
      source: pending.source,
      volId: pending.volId ? String(pending.volId) : undefined,
    }
    step.value = 'select'
    errorMessage.value = '微信授权未绑定到当前账号，请关闭弹窗后重新打赏'
    visible.value = true
    await loadTipAmountOptions()
    if (pending.tipCode) {
      selectedTipCode.value = pending.tipCode
    }
    tipResumeInFlight = false
    return false
  }

  if (fromOAuthError) {
    const retriable =
      fromOAuthError === 'missing_code' || fromOAuthError === 'invalid_callback'
    if (!retriable || hasPendingTipOAuthAutoRetried()) {
      openContext = {
        comicId: String(pending.comicId),
        comicTitle: pending.comicTitle || '',
        source: pending.source,
        volId: pending.volId ? String(pending.volId) : undefined,
      }
      step.value = 'select'
      errorMessage.value = '微信授权未完成，请重试打赏'
      visible.value = true
      await loadTipAmountOptions()
      if (pending.tipCode) {
        selectedTipCode.value = pending.tipCode
      }
      tipResumeInFlight = false
      return false
    }
    markPendingTipOAuthAutoRetried()
  } else {
    clearPendingTipOAuthAutoRetried()
  }

  if (pending.idempotencyKey) {
    idempotencyKey = pending.idempotencyKey
  }

  try {
    stopPolling()
    openContext = {
      comicId: String(pending.comicId),
      comicTitle: pending.comicTitle || '',
      source: pending.source,
      volId: pending.volId ? String(pending.volId) : undefined,
    }
    step.value = 'select'
    errorMessage.value = ''
    qrDataUrl.value = ''
    currentOrderId.value = ''
    currentOutTradeNo.value = ''
    payScene.value = getPayScene()
    visible.value = true

    await loadTipAmountOptions()
    const hasTipCode = tipAmountOptions.value.some((item) => item.code === pending.tipCode)
    if (hasTipCode) {
      selectedTipCode.value = pending.tipCode
      if (resumeSessionId) {
        markTipResumeSessionDone(resumeSessionId)
      }
      await submitTip()
    } else if (pending.tipCode) {
      selectedTipCode.value = pending.tipCode
    }

    return true
  } finally {
    tipResumeInFlight = false
  }
}

export function useComicTip() {
  const isPcQrStep = computed(() => step.value === 'qrcode')
  const isSuccessStep = computed(() => step.value === 'success')
  const selectedTipOption = computed(() => findSelectedTipOption(tipAmountOptions.value))
  const selectedAmountLabel = computed(() => {
    const opt = selectedTipOption.value
    if (opt) return formatTipAmountLabel(opt)
    return ''
  })
  const canSubmitTip = computed(
    () =>
      Boolean(selectedTipCode.value) &&
      tipAmountOptions.value.length > 0 &&
      !tipAmountLoading.value
  )

  return {
    visible,
    step,
    tipAmountOptions,
    tipAmountLoading,
    selectedTipCode,
    loading,
    errorMessage,
    guideMessage,
    guideShareUrl,
    qrDataUrl,
    currentOrderId,
    currentOutTradeNo,
    isConfirmingPayment,
    payScene,
    isPcQrStep,
    isSuccessStep,
    selectedAmountLabel,
    canSubmitTip,
    openComicTip,
    closeComicTip,
    selectTipCode,
    submitTip,
    backToSelect,
    confirmPaymentManually,
  }
}
